import { Component, inject, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { ProviderInvoiceConceptTableModel } from 'src/app/models/tables/shopping/provider-invoice-concept-table.model';
import { ProviderInvoiceDetailTableModel } from 'src/app/models/tables/shopping/provider-invoice-detail-table.model';
import { ProviderInvoiceTableModel } from 'src/app/models/tables/shopping/provider-invoice-table.model';
import { HeaderService } from 'src/app/services/header/header.service';
import { ProviderInvoiceConceptService } from 'src/app/services/shopping/provider-invoice-concept.service';
import { ProviderInvoiceDetailService } from 'src/app/services/shopping/provider-invoice-detail.service';
import { ProviderInvoiceService } from 'src/app/services/shopping/provider-invoice.service';

@Component({
  selector: 'app-provider-invoice',
  templateUrl: './provider-invoice.component.html',
  styleUrls: ['./provider-invoice.component.scss'],
  standalone: false
})
export class ProviderInvoiceComponent implements OnInit {

  private readonly headerService = inject(HeaderService);
  private readonly providerInvoiceConceptService = inject(ProviderInvoiceConceptService);
  private readonly providerInvoiceDetailService = inject(ProviderInvoiceDetailService);
  private readonly providerInvoiceService = inject(ProviderInvoiceService)
  private readonly router = inject(Router);
  private readonly title = inject(Title);

  activeTabIndex = 0;
  ascendant: boolean = true;
  columnSort: string | null = null;
  conceptData: PagedResponseModel<ProviderInvoiceConceptTableModel> = new PagedResponseModel<ProviderInvoiceConceptTableModel>();
  conceptHeader: ColumnResponseModel[] = [];
  detailData: PagedResponseModel<ProviderInvoiceDetailTableModel> = new PagedResponseModel<ProviderInvoiceDetailTableModel>();
  detailHeader: ColumnResponseModel[] = [];
  filters: ColumnResponseModel[] = [];
  generalData: PagedResponseModel<ProviderInvoiceTableModel> = new PagedResponseModel<ProviderInvoiceTableModel>();
  generalHeader: ColumnResponseModel[] = [];
  limit: number = 10;
  nameWindow: string = 'Facturas Proveedores';
  page: number = 1;
  projectName = ProjectName.projectName;
  tableActions: TableAction[] = [
    {
      label: 'Editar',
      icon: 'pi pi-pencil',
      action: 'edit'
    },
  ];

  async ngOnInit() {
    this.headerService.setTitle(this.nameWindow);
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    const filters = localStorage.getItem('provider-invoice');
    if (filters) this.filters = JSON.parse(filters);
    await Promise.all([
      this.getColumnsForGeneralConsultation(),
      this.getProviderInvoicesForGeneralConsultation(),
    ]);
  }

  async exportConceptConsultation() {
    try {
      const blob = await firstValueFrom(this.providerInvoiceConceptService.exportGeneralConsultation(
        this.filters, this.columnSort, this.ascendant
      ));
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Facturas Proveedores Conceptos.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error al exportar los conceptos de las facturas proveedores.', error);
    }
  }

  async exportDetailConsultation() {
    try {
      const blob = await firstValueFrom(this.providerInvoiceDetailService.exportGeneralConsultation(
        this.filters, this.columnSort, this.ascendant
      ));
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Facturas Proveedores Detalles.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error al exportar los detalles de las facturas proveedores.', error);
    }
  }

  async exportGeneralConsultation() {
    try {
      const blob = await firstValueFrom(this.providerInvoiceService.exportGeneralConsultation(
        this.filters, this.columnSort, this.ascendant
      ));
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Facturas Proveedores.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error al exportar las facturas proveedores.', error);
    }
  }

  async getColumnsForConceptConsultation() {
    try {
      if (this.conceptHeader.length === 0) {
        this.conceptHeader = await firstValueFrom(this.providerInvoiceConceptService.getColumnsForGeneralConsultation());
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta de concepto.', error);
    }
  }

  async getColumnsForDetailConsultation() {
    try {
      if (this.detailHeader.length === 0) {
        this.detailHeader = await firstValueFrom(this.providerInvoiceDetailService.getColumnsForGeneralConsultation());
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta de detalle.', error);
    }
  }

  async getColumnsForGeneralConsultation() {
    try {
      if (this.generalHeader.length === 0) {
        this.generalHeader = await firstValueFrom(this.providerInvoiceService.getColumnsForGeneralConsultation());
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta general.', error);
    }
  }

  async getProviderInvoiceConceptsForGeneralConsultation() {
    try {
      this.conceptData = await firstValueFrom(this.providerInvoiceConceptService.getProviderInvoiceConceptsForGeneralConsultation(
        this.page, this.limit, this.columnSort, this.ascendant, this.filters
      ));
    } catch (error) {
      console.error('Error al buscar los conceptos de las facturas de proveedores.', error);
    }
  }

  async getProviderInvoiceDetailsForGeneralConsultation() {
    try {
      this.detailData = await firstValueFrom(this.providerInvoiceDetailService.getProviderInvoiceDetailsForGeneralConsultation(
        this.page, this.limit, this.columnSort, this.ascendant, this.filters
      ));
    } catch (error) {
      console.error('Error al buscar el detalle de las facturas de proveedores.', error);
    }
  }

  async getProviderInvoicesForGeneralConsultation() {
    try {
      this.generalData = await firstValueFrom(this.providerInvoiceService.getProviderInvoicesForGeneralConsultation(
        this.page, this.limit, this.columnSort, this.ascendant, this.filters
      ));
    } catch (error) {
      console.error('Error al buscar las facturas de proveedores.', error);
    }
  }

  public async handleTabChange(tabIndex: number) {
    this.generalHeader.length = this.generalHeader.length === 1 ? 0 : this.generalHeader.length;
    this.detailHeader.length = this.detailHeader.length === 1 ? 0 : this.detailHeader.length;
    this.conceptHeader.length = this.conceptHeader.length === 1 ? 0 : this.conceptHeader.length;

    if (this.activeTabIndex === tabIndex) return;

    this.page = 1;
    this.limit = 10;
    this.activeTabIndex = tabIndex;

    switch (this.activeTabIndex) {
      case 0:
        await Promise.all([
          this.getColumnsForGeneralConsultation(),
          this.getProviderInvoicesForGeneralConsultation()
        ]);
        this.generalData = { ...this.generalData, currentPage: 1, pageSize: 10 };
        break;
      case 1:
        await Promise.all([
          this.getColumnsForDetailConsultation(),
          this.getProviderInvoiceDetailsForGeneralConsultation()
        ]);
        this.detailData = { ...this.detailData, currentPage: 1, pageSize: 10 };
        break;
      case 2:
        await Promise.all([
          this.getColumnsForConceptConsultation(),
          this.getProviderInvoiceConceptsForGeneralConsultation()
        ]);
        this.conceptData = { ...this.conceptData, currentPage: 1, pageSize: 10 };
        break;
    }
  }

  newProviderInvoice() {
    this.router.navigate(['/provider-invoice/register']);
  }

  onAction(event: { action: string, item?: any, selectedItems?: number[] }) {
    if (event.action === 'edit' && event.item) {
      this.router.navigate([`/provider-invoice/register/${event.item.providerInvoiceId}`]);
    }
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters
      .map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index)
      .map((item: any) => item.value);

    this.filters = this.removeDuplicates(filters);
    this.filters.forEach((filter: any) => this.processFilter(filter));
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    switch (this.activeTabIndex) {
      case 0:
        await this.getProviderInvoicesForGeneralConsultation();
        break;
      case 1:
        await this.getProviderInvoiceDetailsForGeneralConsultation();
        break;
      case 2:
        await this.getProviderInvoiceConceptsForGeneralConsultation();
        break;
    }
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filters = this.filters.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filters.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

}
